import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { BatchResult } from "../components/BatchResult";
import { FilterChips } from "../components/FilterChips";
import { Modal } from "../components/Modal";
import { Pagination } from "../components/Pagination";
import { SearchInput } from "../components/SearchInput";
import { SelectionBar } from "../components/SelectionBar";
import { Icon } from "../components/icons";
import {
  Alert,
  Button,
  Card,
  Dropdown,
  EmptyState,
  MENU_ITEM,
  Mono,
  PageHeader,
  Select,
  Table,
  TableSkeleton,
  Tag,
  Td,
  Textarea,
  Th,
  cx,
} from "../components/ui";
import { formatNumber, formatRelative } from "../lib/format";
import {
  CATEGORY_LABELS,
  PRIORITY_LABELS,
  PRIORITY_TONE,
  STATUS_LABELS,
  STATUS_TONE,
  isStale,
  waitingFor,
} from "../lib/support";
import {
  useReplyToTickets,
  useSupportCounts,
  useSupportTickets,
  useUpdateTickets,
} from "../lib/queries";
import type {
  BatchOutcome,
  SupportCategory,
  SupportPriority,
  SupportStatus,
  SupportTicket,
} from "../lib/types";

const LIMIT = 25;

/** A menu in the selection bar, drawn like the secondary buttons beside it. */
const BAR_MENU =
  "h-8 rounded-md border border-line px-2.5 text-sm text-fg hover:border-line-strong hover:bg-raised pointer-coarse:h-auto pointer-coarse:min-h-11";

/** A heading inside that menu. Not an item: the arrow keys skip it. */
const MENU_LABEL = "micro px-2.5 pt-2 pb-1 text-fg-subtle";

/*
 * The queue's tabs, in the order the work is done: what is on us, what is on
 * them, then the archive.
 *
 * `open` is first and is the default view — the console's job here is to answer
 * "what has not been answered", and any other default makes an operator choose
 * a tab before they can start.
 */
const TABS: { value: SupportStatus | ""; label: string; count: (c: Counts) => number | null }[] = [
  { value: "open", label: "Open", count: (c) => c.open },
  { value: "pending", label: "Awaiting customer", count: (c) => c.pending },
  { value: "resolved", label: "Resolved", count: (c) => c.resolved },
  { value: "closed", label: "Closed", count: (c) => c.closed },
  { value: "", label: "All", count: () => null },
];

interface Counts {
  open: number;
  pending: number;
  resolved: number;
  closed: number;
  unassigned: number;
}

const CATEGORIES: { value: SupportCategory; label: string }[] = (
  ["technical", "billing", "account", "abuse", "other"] as SupportCategory[]
).map((value) => ({ value, label: CATEGORY_LABELS[value] }));

const ASSIGNMENT: { value: "me" | "none"; label: string }[] = [
  { value: "me", label: "Mine" },
  { value: "none", label: "Unassigned" },
];

/**
 * Every ticket on the platform, across every tenant.
 *
 * The list the platform did not have: before this, a customer problem lived in
 * a mailbox, which meant the console could show what a tenant *ran* and nothing
 * about what they had asked. Sorted oldest-first while the filter is open —
 * that is the order the work is taken — and newest-first everywhere else, which
 * the API decides rather than this page.
 *
 * Rows can be worked in a batch: taken or released, moved, re-rated, or
 * answered with one reply — the incident that fills the queue with one
 * question. A move that mails every customer in it (resolved, closed) is
 * confirmed first; the rest apply on the click, as they do on one ticket.
 */
export function SupportPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState<SupportStatus | "">("open");
  const [category, setCategory] = useState<SupportCategory | "">("");
  const [priority, setPriority] = useState<SupportPriority | "">("");
  const [assignedTo, setAssignedTo] = useState<"me" | "none" | "">("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);

  const counts = useSupportCounts();
  const { data, isPending, error } = useSupportTickets({
    limit: LIMIT,
    offset,
    ...(status ? { status } : {}),
    ...(category ? { category } : {}),
    ...(priority ? { priority } : {}),
    ...(assignedTo ? { assignedTo } : {}),
    ...(q ? { q } : {}),
  });

  const update = useUpdateTickets();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [confirming, setConfirming] = useState<"resolved" | "closed" | null>(null);
  const [replying, setReplying] = useState(false);
  const [result, setResult] = useState<{ outcomes: BatchOutcome[]; done: string } | null>(null);

  // Any filter change returns to the first page: page four of the old filter is
  // rarely page four of the new one. The selection goes with it — it is one
  // page's, and a batch should never reach rows nobody can see.
  function reset<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setOffset(0);
      setSelected(new Set());
      setResult(null);
    };
  }

  const tally: Counts = counts.data ?? { open: 0, pending: 0, resolved: 0, closed: 0, unassigned: 0 };
  const filtered = Boolean(q || category || priority || assignedTo);

  const rows = data?.data ?? [];
  const chosen = rows.filter((ticket) => selected.has(ticket.id));
  const allOnPage = rows.length > 0 && rows.every((ticket) => selected.has(ticket.id));
  const theirs = chosen.filter((ticket) => ticket.assignedTo && ticket.assignedTo !== user?.id).length;
  const unassigned = chosen.filter((ticket) => !ticket.assignedTo).length;

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const finish = (done: string) => (response: { data: BatchOutcome[] }) => {
    setResult({ outcomes: response.data, done });
    setSelected(new Set());
    setConfirming(null);
    setReplying(false);
  };
  const move = (
    input: { status?: SupportStatus; priority?: SupportPriority; assignedTo?: string | null },
    done: string,
  ) => update.mutate({ ids: chosen.map((ticket) => ticket.id), ...input }, { onSuccess: finish(done) });

  return (
    <>
      <PageHeader
        title="Support"
        description="Every ticket a tenant has opened, and the conversation on each."
      />

      {result && <BatchResult outcomes={result.outcomes} noun="ticket" done={result.done} />}
      {update.error && confirming === null && <Alert>{update.error.message}</Alert>}

      {/*
        Tabs rather than another chip row. Status is the axis the queue is
        organised by — the others narrow whichever slice is being worked — and
        giving it the same weight as "category" said the two were alternatives.
      */}
      <div className="scrollbar-slim -mx-1 flex gap-1 overflow-x-auto px-1">
        {TABS.map((tab) => {
          const active = tab.value === status;
          const count = tab.count(tally);
          return (
            <button
              key={tab.value || "all"}
              type="button"
              aria-pressed={active}
              onClick={() => reset(setStatus)(tab.value)}
              className={cx(
                "flex h-9 shrink-0 items-center gap-2 border px-3 text-sm whitespace-nowrap transition-colors duration-200 ease-smooth pointer-coarse:min-h-11",
                active
                  ? "border-accent/40 bg-accent-soft/50 text-fg"
                  : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
              )}
            >
              {tab.label}
              {count !== null && (
                <span className={cx("tabular-nums", active ? "text-accent-hover" : "text-fg-subtle")}>
                  {count}
                </span>
              )}
            </button>
          );
        })}

        {/* The one number an operator acts on rather than reads: open tickets
            nobody has picked up. A button, because seeing it should be one
            click away from working it. */}
        {tally.unassigned > 0 && (
          <button
            type="button"
            onClick={() => {
              reset(setStatus)("open");
              reset(setAssignedTo)("none");
            }}
            className="ml-auto flex h-9 shrink-0 items-center gap-2 border border-warn/30 bg-warn/8 px-3 text-sm whitespace-nowrap text-warn transition-colors duration-200 ease-smooth hover:border-warn/50"
          >
            <Icon name="warning" className="size-3.5" />
            {tally.unassigned} unassigned
          </button>
        )}
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
          <SearchInput value={q} onChange={reset(setQ)} placeholder="Reference, subject, org or email…" />

          <FilterChips
            value={category}
            onChange={reset(setCategory)}
            options={CATEGORIES}
            allLabel="All kinds"
            ariaLabel="Filter by category"
          />

          {/* Impact as a select, not a fourth chip group. It is the filter an
              operator reaches for least — the queue is worked by age, not by
              what a customer called urgent — and three chip rows on one line
              stop reading as three separate questions. */}
          <div className="w-36">
            <Select
              value={priority}
              onChange={(event) => reset(setPriority)(event.target.value as SupportPriority | "")}
              aria-label="Filter by impact"
            >
              <option value="">Any impact</option>
              {(["urgent", "high", "normal", "low"] as SupportPriority[]).map((value) => (
                <option key={value} value={value}>
                  {PRIORITY_LABELS[value]}
                </option>
              ))}
            </Select>
          </div>

          {/* Labelled rather than ruled off. Two chip groups on one row read as
              one nine-option filter otherwise — "Other" beside "Anyone" looks
              like an alternative to it — and a vertical rule only works while
              the row does not wrap, which at this width it does. */}
          <div className="flex items-center gap-2">
            <span className="micro shrink-0 text-fg-subtle">Assigned</span>
            <FilterChips
              value={assignedTo}
              onChange={reset(setAssignedTo)}
              options={ASSIGNMENT}
              allLabel="Anyone"
              ariaLabel="Filter by assignment"
            />
          </div>

          {!isPending && !error && (
            <p className="ml-auto text-sm text-fg-subtle">
              {formatNumber(data.total)} {data.total === 1 ? "ticket" : "tickets"}
              {filtered && " matching"}
            </p>
          )}
        </div>

        {isPending ? (
          <TableSkeleton columns={6} />
        ) : error ? (
          <div className="p-5">
            <Alert>Could not load the queue. Try again in a moment.</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            icon="support"
            title={status === "open" && !filtered ? "Nothing waiting" : "No tickets match"}
            description={
              status === "open" && !filtered
                ? "Every ticket has been answered. New ones arrive here and by email."
                : "Search covers the reference, the subject, the tenant's name and the address it was opened from — across every status, not just this tab."
            }
            action={
              filtered ? (
                <button
                  type="button"
                  onClick={() => {
                    setQ("");
                    setCategory("");
                    setPriority("");
                    setAssignedTo("");
                    setOffset(0);
                  }}
                  className="border border-line px-3 py-1.5 text-sm text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong hover:text-fg"
                >
                  Clear filters
                </button>
              ) : undefined
            }
          />
        ) : (
          <Table
            head={
              <tr>
                <Th className="w-8">
                  <input
                    type="checkbox"
                    aria-label="Select every ticket on this page"
                    checked={allOnPage}
                    onChange={() =>
                      setSelected(allOnPage ? new Set() : new Set(rows.map((ticket) => ticket.id)))
                    }
                    className="size-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                  />
                </Th>
                <Th>Ticket</Th>
                <Th priority="sm">Tenant</Th>
                <Th priority="md">Kind</Th>
                <Th priority="md">Assignee</Th>
                <Th numeric>Waiting</Th>
                <Th priority="sm" numeric>
                  Last activity
                </Th>
              </tr>
            }
          >
            {data.data.map((ticket) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                selected={selected.has(ticket.id)}
                onToggle={() => toggle(ticket.id)}
              />
            ))}
          </Table>
        )}

        <Pagination
          total={data?.total ?? 0}
          limit={LIMIT}
          offset={offset}
          onChange={(next) => {
            setOffset(next);
            setSelected(new Set());
          }}
        />
      </Card>

      <SelectionBar
        count={chosen.length}
        noun="ticket"
        onClear={() => setSelected(new Set())}
        action={
          <span className="flex flex-wrap items-center justify-end gap-2">
            {/* The same self-service claim as a ticket's own page: take them,
                or put them back. Advisory — anybody can still answer. */}
            <Button
              variant="secondary"
              size="sm"
              disabled={!user}
              loading={update.isPending && update.variables?.assignedTo === user?.id}
              onClick={() => user && move({ assignedTo: user.id }, "taken")}
            >
              Take
            </Button>
            <Dropdown side="top" align="end" trigger={<span>Move ▾</span>} triggerClassName={BAR_MENU}>
              <p className={MENU_LABEL}>Status</p>
              <button type="button" className={MENU_ITEM} onClick={() => move({ status: "open" }, "set to open")}>
                {STATUS_LABELS.open}
              </button>
              <button type="button" className={MENU_ITEM} onClick={() => move({ status: "pending" }, "set to awaiting the customer")}>
                {STATUS_LABELS.pending}
              </button>
              <button type="button" className={MENU_ITEM} onClick={() => setConfirming("resolved")}>
                {STATUS_LABELS.resolved}…
              </button>
              <button type="button" className={MENU_ITEM} onClick={() => setConfirming("closed")}>
                {STATUS_LABELS.closed}…
              </button>
              <p className={cx(MENU_LABEL, "mt-1 border-t border-line")}>Impact</p>
              {(["urgent", "high", "normal", "low"] as SupportPriority[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={MENU_ITEM}
                  onClick={() => move({ priority: value }, `set to ${PRIORITY_LABELS[value].toLowerCase()} impact`)}
                >
                  {PRIORITY_LABELS[value]}
                </button>
              ))}
              <p className={cx(MENU_LABEL, "mt-1 border-t border-line")}>Assignee</p>
              <button type="button" className={MENU_ITEM} onClick={() => move({ assignedTo: null }, "released")}>
                Release
              </button>
            </Dropdown>
            <Button size="sm" onClick={() => setReplying(true)}>
              Reply…
            </Button>
          </span>
        }
      >
        {theirs > 0
          ? `${theirs} assigned to someone else`
          : unassigned > 0
            ? `${unassigned} unassigned`
            : "all yours"}
      </SelectionBar>

      {/*
        Confirmed, unlike the other moves, because each one mails a customer —
        and the list is restated here rather than only counted in the bar: the
        bar is what somebody just changed, this is what they are agreeing to.
      */}
      <Modal
        open={confirming !== null}
        onClose={() => !update.isPending && setConfirming(null)}
        title={`${confirming === "closed" ? "Close" : "Resolve"} ${chosen.length} ticket${chosen.length === 1 ? "" : "s"}`}
        description={
          confirming === "closed"
            ? "Each customer is emailed that their ticket was closed, unless it already was. A closed ticket takes no more replies; they open a new one."
            : "Each customer is emailed that their ticket was resolved, unless it already was. A reply from them reopens it."
        }
        dismissable={!update.isPending}
      >
        <div className="space-y-4">
          {update.error && <Alert>{update.error.message}</Alert>}
          <TicketList tickets={chosen} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirming(null)} disabled={update.isPending}>
              Cancel
            </Button>
            <Button
              loading={update.isPending}
              onClick={() =>
                confirming && move({ status: confirming }, confirming === "closed" ? "closed" : "resolved")
              }
            >
              {confirming === "closed" ? "Close" : "Resolve"} {chosen.length}
            </Button>
          </div>
        </div>
      </Modal>

      <BulkReply
        open={replying}
        tickets={chosen}
        onClose={() => setReplying(false)}
        onDone={(outcomes, internal) => finish(internal ? "noted" : "answered")({ data: outcomes })}
      />
    </>
  );
}

/** The selection, restated inside the dialog that acts on it. */
function TicketList({ tickets }: { tickets: SupportTicket[] }) {
  return (
    <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-fg-subtle">
      {tickets.map((ticket) => (
        <li key={ticket.id} className="truncate">
          <Mono className="text-xs">{ticket.reference}</Mono> <span className="text-fg">{ticket.subject}</span> ·{" "}
          {ticket.orgName ?? "—"}
        </li>
      ))}
    </ul>
  );
}

/**
 * One reply, or one note, on every selected ticket.
 *
 * The composer's two modes, kept: the same toggle, the same warning colour on
 * a note, and the status the reply leaves each ticket in riding along with it.
 * No attachments — a file belongs to one thread, and the same file on twenty is
 * a link that belongs in the text. Each customer gets it on their own ticket,
 * under their own reference; nobody sees who else was written to.
 */
function BulkReply({
  open,
  tickets,
  onClose,
  onDone,
}: {
  open: boolean;
  tickets: SupportTicket[];
  onClose: () => void;
  onDone: (outcomes: BatchOutcome[], internal: boolean) => void;
}) {
  const reply = useReplyToTickets();
  const [internal, setInternal] = useState(false);
  const [body, setBody] = useState("");
  const [status, setStatus] = useState<SupportStatus | "">("");
  const count = tickets.length;

  function close() {
    if (reply.isPending) return;
    reply.reset();
    onClose();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (body.trim().length === 0) return;
    reply.mutate(
      {
        ids: tickets.map((ticket) => ticket.id),
        body: body.trim(),
        internal,
        ...(status && !internal ? { status } : {}),
      },
      {
        onSuccess: (response) => {
          setBody("");
          setStatus("");
          onDone(response.data, internal);
        },
      },
    );
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={internal ? `Add a note to ${count} ticket${count === 1 ? "" : "s"}` : `Reply to ${count} ticket${count === 1 ? "" : "s"}`}
      description={
        internal
          ? "Only operators see it. Nobody is emailed, and the tickets do not move."
          : "Each customer gets it on their own ticket and by email, under their own reference. Nobody sees who else was written to."
      }
      dismissable={!reply.isPending}
    >
      <form onSubmit={submit} className="space-y-4">
        {reply.error && <Alert>{reply.error.message}</Alert>}

        <div className="flex border-b border-line">
          {[
            { value: false, label: "Reply to customers" },
            { value: true, label: "Internal note" },
          ].map((mode) => {
            const active = mode.value === internal;
            return (
              <button
                key={String(mode.value)}
                type="button"
                onClick={() => setInternal(mode.value)}
                aria-pressed={active}
                className={cx(
                  "border-b-2 px-4 py-2.5 text-sm transition-colors duration-200 ease-smooth",
                  active
                    ? mode.value
                      ? "border-warn text-warn"
                      : "border-accent text-fg"
                    : "border-transparent text-fg-subtle hover:text-fg-muted",
                )}
              >
                {mode.label}
              </button>
            );
          })}
        </div>

        <TicketList tickets={tickets} />

        <Textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={5}
          maxLength={10_000}
          aria-label={internal ? "Internal note" : "Reply to the customers"}
          className={internal ? "border-warn/40" : undefined}
          placeholder={
            internal
              ? "What these have in common, and where it is being handled…"
              : "Written once, read by each of them on their own ticket. Plain language; no names."
          }
        />

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          {!internal ? (
            <label className="flex items-center gap-2 text-sm text-fg-subtle">
              then
              <div className="w-44">
                <Select
                  value={status}
                  onChange={(event) => setStatus(event.target.value as SupportStatus | "")}
                  aria-label="Status after sending"
                >
                  <option value="">await the customer</option>
                  <option value="resolved">mark resolved</option>
                  <option value="open">leave open</option>
                  <option value="closed">close it</option>
                </Select>
              </div>
            </label>
          ) : (
            <span />
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={close} disabled={reply.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={reply.isPending} disabled={body.trim().length === 0}>
              <Icon name="send" className="size-3.5" />
              {internal ? `Save on ${count}` : `Send to ${count}`}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function TicketRow({
  ticket,
  selected,
  onToggle,
}: {
  ticket: SupportTicket;
  selected: boolean;
  onToggle: () => void;
}) {
  const tone = STATUS_TONE[ticket.status];
  const waiting = waitingFor(ticket);

  return (
    <tr className={cx("transition-colors duration-200 ease-smooth hover:bg-fg/4", selected && "bg-raised/60")}>
      <Td>
        <input
          type="checkbox"
          aria-label={`Select ${ticket.reference}`}
          checked={selected}
          onChange={onToggle}
          className="size-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
        />
      </Td>
      <Td>
        <Link to={`/support/${ticket.id}`} className="block min-w-0 space-y-1">
          <span className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={cx("size-1.5 shrink-0 rounded-full", tone.dot)}
              title={STATUS_LABELS[ticket.status]}
            />
            <Mono className="text-xs text-fg-subtle">{ticket.reference}</Mono>
            {ticket.priority !== "normal" && ticket.priority !== "low" && (
              <Tag tone={PRIORITY_TONE[ticket.priority] === "bad" ? "bad" : "warn"}>
                {PRIORITY_LABELS[ticket.priority]}
              </Tag>
            )}
          </span>
          <span className="block truncate text-sm text-fg">{ticket.subject}</span>
        </Link>
      </Td>

      <Td priority="sm">
        <div className="min-w-0">
          {/* The tenant, linked to its own page: half of answering a ticket is
              looking at the account it came from. */}
          <Link
            to={`/organizations/${ticket.orgId}`}
            className="block truncate text-sm text-fg-muted transition-colors hover:text-accent-hover"
          >
            {ticket.orgName ?? "—"}
          </Link>
          {ticket.orgSuspendedAt && <span className="text-xs text-danger">Suspended</span>}
        </div>
      </Td>

      <Td priority="md">
        <Tag>{CATEGORY_LABELS[ticket.category]}</Tag>
      </Td>

      <Td priority="md">
        <span className={ticket.assignedToName ? "text-sm text-fg-muted" : "text-sm text-fg-subtle"}>
          {ticket.assignedToName ?? "—"}
        </span>
      </Td>

      <Td numeric>
        {waiting ? (
          <span className={cx("text-sm tabular-nums", isStale(ticket) ? "text-warn" : "text-fg-muted")}>
            {waiting}
          </span>
        ) : (
          <span className="text-sm text-fg-subtle">—</span>
        )}
      </Td>

      <Td priority="sm" numeric>
        <span className="text-sm text-fg-subtle">{formatRelative(ticket.lastMessageAt)}</span>
      </Td>
    </tr>
  );
}
